/**
 * Sprint 23.6 v2 — AgentLearningsCream (right column of Decisions+Learnings).
 *
 * Skim the top of an agent's learnings (Q7): patterns first (highest signal),
 * then a recent feedback memory, max 3 items. Each card shows a category pill +
 * a meta value (confidence for PATTERN, relative time for FEEDBACK / RECENT)
 * and the one-line learning.
 *
 *   PATTERN  — feedback memory carrying the PATTERN_MARKER (find_patterns).
 *   FEEDBACK — high-confidence feedback memory (confidence > 0.7).
 *   RECENT   — most-recent feedback memory not already shown.
 *
 * Visual contract: card = background-secondary, border-radius-md, padding
 * 9px 11px; pills use exact ramp 50/800 hex pairs; body 12px line-height 1.4.
 */

import { useMemo, type CSSProperties } from "react";
import { useNavigate } from "react-router";
import { useAgentLearnings, isPatternMemory } from "../../hooks/use-agent-learnings";
import type { LibraryNode } from "../../../lib/types";

type Bucket = "PATTERN" | "FEEDBACK" | "RECENT";

export interface AgentLearningsCreamProps {
  agentId: string;
  agentName: string;
  /** ISO of when the agent was created — drives the "learning the work" empty state. */
  agentCreatedAt: string;
}

export function AgentLearningsCream({ agentId, agentName }: AgentLearningsCreamProps) {
  const navigate = useNavigate();
  const learnings = useAgentLearnings(agentId);

  const items = useMemo(() => {
    const picked: { node: LibraryNode; bucket: Bucket }[] = [];
    const seen = new Set<string>();

    const patternsByConfidence = [...learnings.patterns].sort(
      (a, b) => (b.confidence ?? 0) - (a.confidence ?? 0),
    );
    for (const p of patternsByConfidence) {
      if (picked.length >= 1) break;
      picked.push({ node: p, bucket: "PATTERN" });
      seen.add(p.id);
    }

    const feedbackByConfidence = [...learnings.feedbackMemories]
      .filter((m) => !isPatternMemory(m))
      .sort((a, b) => (b.confidence ?? 0) - (a.confidence ?? 0));
    for (const f of feedbackByConfidence) {
      if (picked.length >= 2) break;
      if (seen.has(f.id)) continue;
      if ((f.confidence ?? 0) < 0.7) break;
      picked.push({ node: f, bucket: "FEEDBACK" });
      seen.add(f.id);
    }

    const byRecent = [...learnings.feedbackMemories].sort(
      (a, b) => Date.parse(b.created_at) - Date.parse(a.created_at),
    );
    for (const r of byRecent) {
      if (picked.length >= 3) break;
      if (seen.has(r.id)) continue;
      picked.push({ node: r, bucket: "RECENT" });
      seen.add(r.id);
    }

    return picked;
  }, [learnings.feedbackMemories, learnings.patterns]);

  return (
    <div style={column}>
      <h3 style={header}>Learnings</h3>
      {learnings.isLoading ? (
        <div style={emptyText}>Loading…</div>
      ) : items.length === 0 ? (
        <p style={emptyText}>
          {agentName} is learning the work — observations will appear here.
        </p>
      ) : (
        <ul style={list}>
          {items.map(({ node, bucket }) => {
            const palette = PILL[bucket];
            const meta =
              bucket === "PATTERN"
                ? typeof node.confidence === "number"
                  ? node.confidence.toFixed(2)
                  : null
                : relativeTime(node.created_at);
            return (
              <li
                key={node.id}
                style={card}
                onClick={() => navigate(`/agents/${agentId}#memories`)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter") navigate(`/agents/${agentId}#memories`);
                }}
              >
                <div style={pillRow}>
                  <span style={{ ...pillBase, background: palette.bg, color: palette.fg }}>
                    {bucket}
                  </span>
                  {meta && <span style={metaText}>{meta}</span>}
                </div>
                <div style={cardBody}>{learningBody(node)}</div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

const PILL: Record<Bucket, { bg: string; fg: string }> = {
  PATTERN: { bg: "#EAF3DE", fg: "#173404" },
  FEEDBACK: { bg: "#E1F5EE", fg: "#04342C" },
  RECENT: { bg: "#FAEEDA", fg: "#412402" },
};

/** The one-line learning: prefer the memory name, fall back to its first body line. */
function learningBody(node: LibraryNode): string {
  const name = node.name?.trim();
  if (name) return truncate(name, 80);
  return node.document ? truncate(firstLines(node.document), 80) : "";
}

function firstLines(doc: string): string {
  let body = doc;
  if (body.startsWith("---")) {
    const end = body.indexOf("\n---", 3);
    if (end >= 0) body = body.slice(end + 4);
  }
  const lines = body
    .split("\n")
    .map((l) => l.trim())
    .filter((l) => l.length > 0);
  return lines.slice(0, 2).join(" ");
}

function truncate(text: string, max: number): string {
  const trimmed = text.trim();
  return trimmed.length > max ? `${trimmed.slice(0, max - 1)}…` : trimmed;
}

function relativeTime(iso: string): string {
  const t = Date.parse(iso.includes("T") ? iso : `${iso.replace(" ", "T")}Z`);
  if (Number.isNaN(t)) return "—";
  const sec = Math.max(0, Math.floor((Date.now() - t) / 1000));
  const day = Math.floor(sec / 86_400);
  if (day < 1) return "today";
  if (day === 1) return "1d ago";
  if (day < 30) return `${day}d ago`;
  const mo = Math.floor(day / 30);
  return mo < 12 ? `${mo}mo ago` : `${Math.floor(mo / 12)}y ago`;
}

const column: CSSProperties = {
  minWidth: 0,
};

const header: CSSProperties = {
  margin: "0 0 6px",
  fontSize: 12,
  fontWeight: 500,
  color: "var(--color-text-secondary)",
  textTransform: "uppercase",
  letterSpacing: "0.5px",
};

const list: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 6,
  margin: 0,
  padding: 0,
  listStyle: "none",
};

const card: CSSProperties = {
  background: "var(--color-background-secondary)",
  borderRadius: "var(--border-radius-md)",
  padding: "9px 11px",
  cursor: "pointer",
};

const pillRow: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 6,
  marginBottom: 3,
};

const pillBase: CSSProperties = {
  fontSize: 9,
  padding: "1px 6px",
  borderRadius: 999,
  fontWeight: 600,
  letterSpacing: "0.04em",
};

const metaText: CSSProperties = {
  fontSize: 10,
  color: "var(--color-text-tertiary)",
};

const cardBody: CSSProperties = {
  fontSize: 12,
  lineHeight: 1.4,
  color: "var(--color-text-primary)",
};

const emptyText: CSSProperties = {
  margin: 0,
  fontSize: 12,
  lineHeight: 1.5,
  color: "var(--color-text-secondary)",
};
